import React, { useState } from 'react'
import { Button, Card, Col, Input, InputNumber, Row, Select, Slider, Table, Typography } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons'
import nutritionalGoalsData from '../utils/nutritionalGoals.json'

const { Title, Text } = Typography
const { Option } = Select

const NutritionalGoals = ({ data, setData }) => {
  const [selectedGoalType, setSelectedGoalType] = useState('')
  const [targetValue, setTargetValue] = useState('')
  const [unit, setUnit] = useState('')
  const [timeframe, setTimeframe] = useState('daily')
  const [notes, setNotes] = useState('')

  const goalTypes = nutritionalGoalsData.goalTypes
  const units = nutritionalGoalsData.units
  const timeframes = nutritionalGoalsData.timeframes

  const handleAddGoal = () => {
    if (selectedGoalType && targetValue && unit) {
      const newGoal = {
        id: Date.now(),
        goalType: selectedGoalType,
        targetValue,
        unit,
        timeframe,
        notes
      }

      setData([...data, newGoal])

      // Reset form
      setSelectedGoalType('')
      setTargetValue('')
      setUnit('')
      setTimeframe('daily')
      setNotes('')
    }
  }

  const handleRemoveGoal = (id) => {
    setData(data.filter((item) => item.id !== id))
  }

  // Render appropriate input based on goal type
  const renderValueInput = () => {
    if (!selectedGoalType) return null

    const goalInfo = goalTypes.find((g) => g.name === selectedGoalType)

    // For macro goals that use percentages and sliders
    if (goalInfo?.inputType === 'percentage') {
      return (
        <div>
          <Slider
            value={Number(targetValue) || 0}
            onChange={(value) => setTargetValue(value.toString())}
            min={0}
            max={100}
            tipFormatter={(value) => `${value}%`}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Text>0%</Text>
            <Text type='secondary'>{targetValue || 0}%</Text>
            <Text>100%</Text>
          </div>
        </div>
      )
    }

    // For numeric inputs
    return (
      <InputNumber
        style={{ width: '100%' }}
        placeholder='Enter target value'
        value={targetValue}
        onChange={(value) => setTargetValue(value)}
        min={0}
      />
    )
  }

  // Filter units based on goal type
  const getFilteredUnits = () => {
    if (!selectedGoalType) return units

    const goalInfo = goalTypes.find((g) => g.name === selectedGoalType)
    if (goalInfo?.inputType === 'percentage') {
      return units.filter((u) => u === '%')
    }

    // For weight goals, filter to weight units
    if (selectedGoalType.toLowerCase().includes('weight')) {
      return units.filter((u) => ['kg', 'lb', 'g'].includes(u))
    }

    // For calorie goals
    if (selectedGoalType.toLowerCase().includes('calor')) {
      return units.filter((u) => ['kcal', 'cal'].includes(u))
    }

    return units
  }

  const columns = [
    {
      title: 'Goal Type',
      dataIndex: 'goalType',
      key: 'goalType'
    },
    {
      title: 'Target',
      key: 'target',
      render: (record) => `${record.targetValue} ${record.unit}`
    },
    {
      title: 'Timeframe',
      dataIndex: 'timeframe',
      key: 'timeframe'
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          danger
          icon={<FontAwesomeIcon icon={faTrash} />}
          onClick={() => handleRemoveGoal(record.id)}
          size='small'
        />
      )
    }
  ]

  return (
    <div>
      <Title level={4}>Nutritional Goals & Tracking</Title>
      <Text>Set personalized nutritional targets and goals for the meal plan.</Text>

      <Card style={{ marginTop: 16, marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Text strong>Goal Type</Text>
            <Select
              placeholder='Select nutritional goal'
              style={{ width: '100%' }}
              value={selectedGoalType}
              onChange={(value) => {
                setSelectedGoalType(value)

                // Reset unit when goal type changes
                setUnit('')

                // Set default unit based on goal type
                const goalInfo = goalTypes.find((g) => g.name === value)
                if (goalInfo?.inputType === 'percentage') {
                  setUnit('%')
                } else if (value.toLowerCase().includes('calor')) {
                  setUnit('kcal')
                } else if (value.toLowerCase().includes('weight')) {
                  setUnit('kg')
                } else if (value.toLowerCase().includes('protein')) {
                  setUnit('g')
                }
              }}
            >
              {goalTypes.map((goal) => (
                <Option key={goal.name} value={goal.name}>
                  {goal.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} md={12}>
            <Text strong>Target Value</Text>
            {renderValueInput()}
          </Col>

          <Col xs={24} md={12}>
            <Text strong>Unit</Text>
            <Select
              placeholder='Select unit'
              style={{ width: '100%' }}
              value={unit}
              onChange={(value) => setUnit(value)}
            >
              {getFilteredUnits().map((unit) => (
                <Option key={unit} value={unit}>
                  {unit}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} md={12}>
            <Text strong>Timeframe</Text>
            <Select
              placeholder='Select timeframe'
              style={{ width: '100%' }}
              value={timeframe}
              onChange={(value) => setTimeframe(value)}
            >
              {timeframes.map((time) => (
                <Option key={time} value={time}>
                  {time}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={24}>
            <Text strong>Additional Notes</Text>
            <Input.TextArea
              rows={4}
              placeholder='Enter any additional notes or guidelines for this nutritional goal'
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Col>

          <Col span={24}>
            <Button
              type='primary'
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={handleAddGoal}
              disabled={!selectedGoalType || !targetValue || !unit}
            >
              Add Nutritional Goal
            </Button>
          </Col>
        </Row>
      </Card>

      <Table dataSource={data} columns={columns} rowKey='id' pagination={false} size='small' />
    </div>
  )
}

export default NutritionalGoals
